"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PremiumDfwTreeCare() {
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: subHeadingRef,
    isVisible: subHeadingVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: paraRef,
    isVisible: paraVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: statRef,
    isVisible: statVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="976" id="premium-dfw-tree-care" className="relative overflow-hidden bg-background min-h-[92vh] flex items-center py-20 lg:py-0 text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse at bottom left, hsla(165, 25%, 6%, 0.05), transparent 55%), radial-gradient(ellipse at bottom right, hsla(75, 95%, 45%, 0.05), transparent 60%)"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.02] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")"
    }} aria-hidden="true" />

      <div className="relative w-full max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[55fr_45fr] gap-12 lg:gap-16 items-center">
          {}
          <div className="relative z-10">
            {}
            <div ref={labelRef} className={`flex items-center gap-3 mb-8 transition-all duration-700 ease-out ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              <span className="block h-px w-8 bg-accent" aria-hidden="true" />
              <span className="text-xs tracking-[0.2em] uppercase text-secondary font-mono" style={{
              fontFamily: "var(--font-mono)"
            }}>
                01 // Certified Arborist Expertise
              </span>
            </div>

            {}
            <h2 ref={headingRef} className="text-foreground font-default" style={{
            fontWeight: 800,
            letterSpacing: "-0.04em",
            lineHeight: 0.95,
            fontSize: "clamp(2.5rem, 8vw, 5.5rem)"
          }}>
              {["The", "surgical", "precision", "of", "expert"].map((word, i) => <span data-index={i} key={i} className="inline-block mr-[0.25em] transition-all duration-700 ease-out" style={{
              opacity: headingVisible ? 1 : 0,
              transform: headingVisible ? "translateY(0)" : "translateY(20px)",
              transitionDelay: `${i * 90}ms`
            }}>
                  {word}
                </span>)}
              <span className="inline-block text-accent transition-all duration-700 ease-out" style={{
              fontWeight: 800,
              opacity: headingVisible ? 1 : 0,
              transform: headingVisible ? "translateY(0)" : "translateY(20px)",
              transitionDelay: "450ms"
            }}>
                care.
              </span>
            </h2>

            <h2 ref={subHeadingRef} className={`mt-6 text-xl lg:text-2xl font-serif text-foreground/90 transition-all duration-700 ease-out delay-150 ${subHeadingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              Professional Arborist Care for DFW's Premier Landscapes.
            </h2>

            {}
            <p ref={paraRef} className={`mt-8 max-w-[480px] text-base lg:text-lg leading-relaxed text-muted-foreground font-default transition-all duration-700 ease-out delay-300 ${paraVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
            color: "var(--secondary)",
            opacity: paraVisible ? 0.9 : 0
          }}>
              We provide bespoke tree care across the DFW area, tailored for homeowners who value the long-term health and architectural beauty of their landscape. Schedule your <strong>free ISA arborist consultation</strong> today to ensure your Tarrant County property receives the master-level attention it deserves.
            </p>

            {}
            <div ref={ctaRef} className={`mt-10 flex flex-wrap items-center gap-6 transition-all duration-700 ease-out delay-500 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              <Button asChild size="lg" className="rounded-md bg-accent text-accent-foreground hover:bg-accent/90 px-7 h-12 text-base font-bold shadow-md border-none">
                <Link to="/GetQuotePage">Get a Quote</Link>
              </Button>

              <Link to="/OurExpertisePage" className="group inline-flex items-center gap-2 text-foreground hover:text-accent transition-colors text-base font-mono uppercase tracking-wider">
                <span className="border-b border-foreground/30 group-hover:border-accent pb-0.5">
                  View Services
                </span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="relative">
            {}
            <div className="absolute -top-8 -right-6 lg:-right-10 w-48 h-48 lg:w-64 lg:h-64 bg-accent/20 opacity-80 z-0" style={{
            borderRadius: "0px",
            border: "1px solid var(--accent)"
          }} aria-hidden="true" />

            {}
            <div ref={imageRef} className={`relative z-10 overflow-hidden transition-all duration-1000 ease-out rounded-md shadow-lg ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            aspectRatio: "3 / 4"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5261.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5261.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5261.jpg-wvc-sizes" alt="Professional arborist performing precision pruning on a large Texas oak tree" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale-[0.2] contrast-[1.1]" />
              {}
              <div className="absolute inset-0 opacity-[0.15] mix-blend-overlay pointer-events-none" style={{
              backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
            }} aria-hidden="true" />
            </div>

            {}
            <div ref={statRef} className={`absolute z-20 bottom-[-28px] left-[-20px] lg:left-[-40px] bg-card shadow-xl rounded-md px-6 py-5 max-w-[260px] border border-border transition-all duration-700 ease-out delay-700 ${statVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div className="flex items-baseline gap-2">
                <span className="text-foreground font-mono" style={{
                fontWeight: 700,
                fontSize: "2.25rem",
                lineHeight: 1,
                letterSpacing: "-0.02em"
              }}>
                  25+
                </span>
              </div>
              <p className="mt-2 text-xs uppercase tracking-widest text-secondary font-mono">
                Years of DFW Canopy Management
              </p>
            </div>
          </div>
        </div>

        {}
        <div className="mt-20 lg:mt-28 flex items-center justify-center opacity-30">
          <svg width="180" height="14" viewBox="0 0 180 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M0 7H180" stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 4" fill="none" />
            <circle cx="90" cy="7" r="3" fill="var(--accent)" />
          </svg>
        </div>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Arbor Pro",
        "description": "Premium residential tree service and ISA certified arborist consultations in DFW, Fort Worth, and Tarrant County.",
        "areaServed": [{
          "@type": "City",
          "name": "Fort Worth"
        }, {
          "@type": "City",
          "name": "Dallas"
        }, {
          "@type": "State",
          "name": "Texas"
        }, {
          "@type": "AdministrativeArea",
          "name": "Tarrant County"
        }],
        "hasCertification": "ISA Certified Arborist"
      })
    }} />
    </section>;
}